// @ts-nocheck
import { gql } from "@apollo/client";

// The public share page's toPanelDescriptor maps panels to the operation
// names defined here (marketSummary and the five breakdowns below); piq
// keeps its own copies of these same documents in piq-customer-ui's
// routes/__utils__/shareQueries.js, which must stay identical to these.

// NOTE: SEGMENT_ANALYSIS_NAICS_BREAKDOWN_QUERY, MARKET_SUMMARY_QUERY,
// SPEND_BY_REVENUE_RANGE_QUERY, SPEND_BY_EMPLOYEES_RANGE_QUERY,
// SEGMENT_ANALYSIS_COUNTRY_BREAKDOWN_QUERY, SEGMENT_ANALYSIS_INDUSTRY_BREAKDOWN
// and GMA_SPEND_CATEGORY_BREAKDOWN_QUERY below carry the new
// tenantId/modelScores/modelScoreFilter args added for the Market Sizing tab.
// The spend-category breakdown's three came last and need company-search-api
// PR #1310 (on top of hermes-api PR #1156) deployed before this repo ships
// them — the same additive-arg rule as the rest of this note. These same operations are also used by the pre-existing
// MarketLandscape view (useGetSpendBy*Data hooks), unconditionally, with no
// feature-flag gate on the query shape itself. An additive GraphQL arg is a
// hard schema-validation error until the server accepts it, so this PR must
// not merge/deploy before company-search-api PR #1308 (and hermes-api PR
// #1100) — merging frontend-first would break every MarketLandscape
// breakdown for every user, not just the flagged Market Sizing tab.

export const MARKET_INTELLIGENCE_NAICS_QUERY = gql`
  query getNAICS {
    naics2012 {
      count
      data {
        id
        code
        name
        parentId
      }
    }
  }
`;

export const GMA_GEO_SPEND_QUERY = gql`
  query globalMarketAnalysisSpendByRegion(
    $companyFilters: OpportunityGeneratorFilter
    $spendCategoryIds: [Identifier]
    $customSpendCategory: CustomSpendCategory
    $localizeInstalls: Boolean
    $companyMatchFile: CompanyMatchFilter
  ) {
    globalMarketAnalysisGeopoliticalRegionBreakdown(
      companyFilters: $companyFilters
      spendCategoryIds: $spendCategoryIds
      localizeInstalls: $localizeInstalls
      customSpendCategory: $customSpendCategory
      companyMatchFile: $companyMatchFile
    ) {
      companyCount
      totalSpend
      regionId
      regionName
    }
  }
`;

export const SEGMENT_ANALYSIS_NAICS_BREAKDOWN_QUERY = gql`
  query segmentAnalysisNaicsBreakdown(
    $companyFilters: OpportunityGeneratorFilter
    $customSpendCategory: CustomSpendCategory
    $localizeInstalls: Boolean
    $spendCategoryIds: [Identifier]
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    segmentAnalysisNaicsBreakdown(
      companyFilters: $companyFilters
      customSpendCategory: $customSpendCategory
      localizeInstalls: $localizeInstalls
      spendCategoryIds: $spendCategoryIds
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
      naicsId
    }
  }
`;

export const MARKET_SUMMARY_QUERY = gql`
  query marketSummary(
    $companyFilters: OpportunityGeneratorFilter
    $spendCategoryIds: [Identifier]
    $customSpendCategory: CustomSpendCategory
    $localizeInstalls: Boolean
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    totalMarketSummary(
      companyFilters: $companyFilters
      spendCategoryIds: $spendCategoryIds
      customSpendCategory: $customSpendCategory
      localizeInstalls: $localizeInstalls
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
    }
  }
`;

export const SPEND_BY_REVENUE_RANGE_QUERY = gql`
  query segmentAnalysisRevenueRangeBreakdown(
    $opportunityGeneratorFilters: OpportunityGeneratorFilter
    $localizeInstalls: Boolean
    $spendCategoryIds: [Identifier]
    $customSpendCategory: CustomSpendCategory
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    segmentAnalysisRevenueRangeBreakdown(
      companyFilters: $opportunityGeneratorFilters
      localizeInstalls: $localizeInstalls
      spendCategoryIds: $spendCategoryIds
      customSpendCategory: $customSpendCategory
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
      range {
        min
        max
      }
    }
  }
`;

export const SPEND_BY_EMPLOYEES_RANGE_QUERY = gql`
  query segmentAnalysisEmployeeRangeBreakdown(
    $opportunityGeneratorFilters: OpportunityGeneratorFilter!
    $localizeInstalls: Boolean
    $spendCategoryIds: [Identifier]
    $customSpendCategory: CustomSpendCategory
    $ranges: [InputIntegerRange]
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    segmentAnalysisEmployeeRangeBreakdown(
      companyFilters: $opportunityGeneratorFilters
      localizeInstalls: $localizeInstalls
      spendCategoryIds: $spendCategoryIds
      customSpendCategory: $customSpendCategory
      ranges: $ranges
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
      range {
        min
        max
      }
    }
  }
`;

export const SEGMENT_ANALYSIS_COUNTRY_BREAKDOWN_QUERY = gql`
  query countrySpendBreakdown(
    $companyFilters: OpportunityGeneratorFilter
    $customSpendCategory: CustomSpendCategory
    $localizeInstalls: Boolean
    $spendCategoryIds: [Identifier]
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
    $limit: Int
  ) {
    segmentAnalysisCountryBreakdown(
      companyFilters: $companyFilters
      customSpendCategory: $customSpendCategory
      localizeInstalls: $localizeInstalls
      spendCategoryIds: $spendCategoryIds
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
      limit: $limit
    ) {
      companyCount
      totalSpend
      countryId
      countryName
      averageKnownSpend
    }
  }
`;

export const SEGMENT_ANALYSIS_INDUSTRY_BREAKDOWN = gql`
  query segmentAnalysisIndustryBreakdown(
    $companyFilters: OpportunityGeneratorFilter
    $customSpendCategory: CustomSpendCategory
    $localizeInstalls: Boolean
    $spendCategoryIds: [Identifier]
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    segmentAnalysisIndustryBreakdown(
      companyFilters: $companyFilters
      customSpendCategory: $customSpendCategory
      localizeInstalls: $localizeInstalls
      spendCategoryIds: $spendCategoryIds
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
      industryId
    }
  }
`;

// NOTE: this document is PINNED in piq-customer-ui
// (routes/__utils__/shareQueries.js) for the public share page, which runs its
// own copy rather than forwarding this one, and that copy must stay
// byte-identical. The scoring args below therefore also need the matching piq
// change — without it a shared link narrows its headline by tier while these
// cards still answer for the whole market.
export const GMA_SPEND_CATEGORY_BREAKDOWN_QUERY = gql`
  query globalMarketAnalysisSpendCategoryBreakdown(
    $companyFilters: OpportunityGeneratorFilter
    $spendCategoryIds: [Identifier]
    $localizeInstalls: Boolean
    $companyMatchFile: CompanyMatchFilter
    $tenantId: String
    $modelScores: [ModelScoreInput]
    $modelScoreFilter: [ModelScoreFilterInput]
  ) {
    globalMarketAnalysisSpendCategoryBreakdown(
      companyFilters: $companyFilters
      spendCategoryIds: $spendCategoryIds
      localizeInstalls: $localizeInstalls
      companyMatchFile: $companyMatchFile
      tenantId: $tenantId
      modelScores: $modelScores
      modelScoreFilter: $modelScoreFilter
    ) {
      companyCount
      totalSpend
      spendCategoryId
      spendCategoryName
    }
  }
`;

export const TRENDING_DATA_BY_PRODUCTS_QUERY = gql`
  query marketTimeSeriesProductBreakdown(
    $companyFilters: OpportunityGeneratorFilter
    $lookBackYears: Int
    $localizeInstalls: Boolean
    $metric: MarketTimeSeriesMetric!
    $period: MarketTimeSeriesPeriod!
    $productIds: [Int]!
    $companyMatchFile: CompanyMatchFilter
  ) {
    marketTimeSeriesProductBreakdown(
      companyFilters: $companyFilters
      lookBackYears: $lookBackYears
      localizeInstalls: $localizeInstalls
      period: $period
      productIds: $productIds
      metric: $metric
      companyMatchFile: $companyMatchFile
    ) {
      hits
      monthStartDate
      productId
    }
  }
`;
